export type Listener<T> = (payload: T) => void;

/**
 * Émetteur typé minimal. Il existe pour satisfaire la contrainte « core/
 * ignore le jeu » : les modules de core/ signalent, ils n'appellent pas.
 * Aucune lib pour ça, le besoin tient en trente lignes.
 */
export class Emitter<Events extends Record<string, unknown>> {
  private listeners: { [K in keyof Events]?: Set<Listener<Events[K]>> } = {};

  /** Retourne la fonction de désabonnement, plus sûre qu'un off() symétrique. */
  on<K extends keyof Events>(
    type: K,
    listener: Listener<Events[K]>,
  ): () => void {
    const set = this.listeners[type] ?? new Set<Listener<Events[K]>>();
    this.listeners[type] = set;
    set.add(listener);
    return () => this.off(type, listener);
  }

  once<K extends keyof Events>(
    type: K,
    listener: Listener<Events[K]>,
  ): () => void {
    const off = this.on(type, (payload) => {
      off();
      listener(payload);
    });
    return off;
  }

  off<K extends keyof Events>(type: K, listener: Listener<Events[K]>): void {
    this.listeners[type]?.delete(listener);
  }

  emit<K extends keyof Events>(type: K, payload: Events[K]): void {
    const set = this.listeners[type];
    if (!set) return;
    // Copie : un listener peut se désabonner pendant l'émission.
    for (const listener of [...set]) listener(payload);
  }

  dispose(): void {
    this.listeners = {};
  }
}
